import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  FUNNEL_STAGE_LABEL,
  IMOVEL_LABEL,
  LOCAL_LABEL,
  MOTIVO_LABEL,
  PAGAMENTO_LABEL,
  PRAZO_LABEL,
  PROPRIEDADE_LABEL,
  REGIAO_LABEL,
  formatBRL,
  formatCep,
  formatPhone,
  type FunnelStageKey,
} from '@sc/shared/domain';
import { ExampleBadge, StageBadge, TierBadge } from '../components/badges';
import { HistoryList, NotesList } from '../components/LeadHistory';
import { StatusForm, type StatusFormValue } from '../components/StatusForm';
import { api, ApiError } from '../lib/api';
import { formatDateTime, formatTime, formatUsdMicro } from '../lib/format';
import type { LeadDetail } from './types';

const EVENT_LABEL: Record<string, string> = {
  abriu_chat: 'Abriu o chat',
  aviso_enviado: 'Aviso enviado por e-mail à equipe',
};

function eventLabel(e: LeadDetail['timeline'][number]) {
  if (e.type === 'etapa') {
    const st = (e.payload?.stage as FunnelStageKey) ?? 'iniciou';
    const motivo = e.payload?.motivo as keyof typeof MOTIVO_LABEL | undefined;
    return `${FUNNEL_STAGE_LABEL[st] ?? 'Avançou no chat'}${motivo ? `: ${MOTIVO_LABEL[motivo] ?? motivo}` : ''}`;
  }
  return EVENT_LABEL[e.type] ?? e.type.replace(/_/g, ' ');
}

const label = (map: Record<string, string>, k: string | null) => (k ? (map[k] ?? k) : null);

/** Respostas da triagem em pares rótulo/valor, na ordem do chat. */
function respostas(l: LeadDetail['lead']): [string, string][] {
  const linhas: [string, string | null][] = [
    ['Atendimento', l.objective === 'manutencao' ? 'Manutenção de sistema existente' : l.objective ? 'Instalação' : null],
    ['Problema', l.problem],
    ['Imóvel', label(IMOVEL_LABEL, l.propertyType)],
    ['Conta de luz', l.monthlyBillCents !== null ? `${l.billApproximate ? 'Cerca de ' : ''}${formatBRL(l.monthlyBillCents)} por mês` : null],
    ['Localização', l.cep ? `${formatCep(l.cep)} · ${label(REGIAO_LABEL, l.region)}` : l.city],
    ['Imóvel próprio', label(PROPRIEDADE_LABEL, l.ownership)],
    ['Painéis', label(LOCAL_LABEL, l.panelLocation)],
    ['Pagamento', label(PAGAMENTO_LABEL, l.payment)],
    ['Prazo', label(PRAZO_LABEL, l.timeframe)],
    [
      'Pré-análise',
      l.estimatedKwp && l.estimatedPanels && l.savingsMinCents !== null && l.savingsMaxCents !== null
        ? `${Number(l.estimatedKwp).toLocaleString('pt-BR')} kWp · ${l.estimatedPanels} painéis · economia de ${formatBRL(l.savingsMinCents)} a ${formatBRL(l.savingsMaxCents)}/mês`
        : null,
    ],
  ];
  return linhas.filter((x): x is [string, string] => !!x[1]);
}

export function LeadDetailPanel({ leadId, onClose }: { leadId: number; onClose: () => void }) {
  const qc = useQueryClient();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [showChat, setShowChat] = useState(false);
  const [saved, setSaved] = useState(false);
  const q = useQuery({
    queryKey: ['admin', 'lead', leadId],
    queryFn: () => api.get<LeadDetail>(`/admin/leads/${leadId}`),
  });

  useEffect(() => {
    setShowChat(false);
    setSaved(false);
    headingRef.current?.focus();
  }, [leadId]);

  const save = useMutation({
    mutationFn: (body: StatusFormValue) => api.patch(`/admin/leads/${leadId}`, body),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: () => setSaved(false),
  });

  const d = q.data;
  const l = d?.lead;
  return (
    <aside className="detail" aria-label="Detalhe do lead">
      <div className="detail-head">
        <h2 tabIndex={-1} ref={headingRef}>
          Lead #{leadId}
        </h2>
        {l && <StageBadge stage={l.displayStage} />}
        <button type="button" className="detail-close" aria-label="Fechar detalhe" onClick={onClose}>
          ×
        </button>
      </div>
      {q.isLoading && <p className="small">Carregando…</p>}
      {q.isError && <p className="alert alert-error">Não foi possível carregar o lead.</p>}
      {d && l && (
        <>
          {l.isExample && <ExampleBadge />}
          <div className="block">
            <span className="label">Prioridade</span>
            <p className="text">
              <TierBadge tier={l.tier} score={l.score} senior={l.senior} manutencao={l.objective === 'manutencao'} />
            </p>
          </div>

          {l.forwardedAt && (
            <div className="block">
              <span className="label">Contato</span>
              <p className="text">
                {l.name ?? 'Sem nome'} · <span className="mono">{formatPhone(l.whatsapp)}</span>
                {l.email && (
                  <>
                    <br />
                    {l.email}
                  </>
                )}
                <br />
                Protocolo <span className="mono">{l.protocol}</span>
                {!l.notifiedAt && (
                  <>
                    <br />
                    <span className="badge tone-yellow">Aviso por e-mail pendente</span>
                  </>
                )}
              </p>
              {l.whatsapp && (
                <a className="btn btn-primary btn-block" href={`https://wa.me/${l.whatsapp}`} target="_blank" rel="noreferrer noopener">
                  Chamar no WhatsApp
                </a>
              )}
            </div>
          )}

          <div className="block">
            <span className="label">Respostas da triagem</span>
            {respostas(l).length ? (
              <dl className="answers">
                {respostas(l).map(([k, v]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="small">Ainda não respondeu nada.</p>
            )}
            {l.ineligibilityReason && <p className="small">Encerrado: {MOTIVO_LABEL[l.ineligibilityReason]}.</p>}
          </div>

          <div className="block">
            <span className="label">Origem</span>
            <p className="text">
              {l.utmSource ?? 'Direto'}
              {l.utmCampaign ? ` · ${l.utmCampaign}` : ''} · {formatDateTime(l.createdAt)}
              {l.pageUrl && (
                <>
                  <br />
                  <span className="small">Página: {l.pageUrl.replace(/^https?:\/\/(www\.)?/, '')}</span>
                </>
              )}
            </p>
          </div>

          <div className="block">
            <span className="label">Linha do tempo</span>
            {d.timeline.length ? (
              <ol className="timeline">
                {d.timeline.map((e, i) => (
                  <li key={i}>
                    {eventLabel(e)} <span className="when">· {formatTime(e.createdAt)}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="small">Sem eventos.</p>
            )}
          </div>

          {d.conversation && (
            <div className="block">
              <span className="label">Conversa</span>
              <p className="small">
                {d.conversation.turns} respostas · {d.conversation.aiCalls} chamadas à IA · {formatUsdMicro(d.conversation.costMicroUsd)}
                {d.conversation.usedFallback ? ' · IA indisponível em algum turno' : ''}
              </p>
              <button type="button" className="btn-link" aria-expanded={showChat} onClick={() => setShowChat((v) => !v)}>
                {showChat ? 'Esconder conversa' : 'Ver conversa completa'}
              </button>
              {showChat && (
                <div className="transcript">
                  {d.conversation.messages.map((m, i) => (
                    <div key={i} className={`bubble bubble-${m.role}${m.filtered ? ' bubble-filtered' : ''}`}>
                      <span className="sr-only">{m.role === 'user' ? 'Pessoa: ' : 'Assistente: '}</span>
                      {m.content}
                      {m.filtered && <span className="small"> (resposta da IA trocada pelo filtro)</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <StatusForm
            statusLabel="Etapa do atendimento"
            status={l.commercialStatus}
            closedValueCents={l.closedValueCents}
            saving={save.isPending}
            saved={saved}
            error={save.error instanceof ApiError ? save.error.message : save.error ? 'Não foi possível salvar.' : null}
            onSave={(v) => {
              setSaved(false);
              save.mutate(v);
            }}
          />
          {l.closedValueCents != null && (
            <p className="small">
              Projeto fechado: <span className="mono">{formatBRL(l.closedValueCents)}</span>
            </p>
          )}

          <div className="block">
            <span className="label">Histórico de status</span>
            <HistoryList history={d.history} />
          </div>
          {d.notes.length > 0 && (
            <div className="block">
              <span className="label">Anotações</span>
              <NotesList notes={d.notes} />
            </div>
          )}
        </>
      )}
    </aside>
  );
}
